/**
 * Vehicle detail (`/flotte/:idNpol/:idVeh` — fiches 22809 / 22811 / 22768 /
 * 22776 / 22778).
 *
 * - Eager, parallel fetch of FIC_VEH, FIC_POL_VEH, TAB_GARANTIES_VEH,
 *   TAB_SIN_VEH and TAB_DOCS_VEH (`useVehicleDetailData`); each card owns its
 *   loading / error / empty state.
 * - Header: title `MARQ_VEH MODL_VEH` (router state `marqueModele` of the
 *   list row while FIC_VEH loads or when it fails), subtitle `IMMAT_VEH`,
 *   breadcrumb Flotte auto › IMMAT_VEH.
 * - `Tabs` with stable values `details`, `contrat`, `garanties`,
 *   `sinistres` (claims count), `documents`, each rendering one registered
 *   card (`components/fleet/register.ts`).
 * - Rule context: base bag + `vehicle_id`, `contract_id`, `vehicle_status`
 *   (VEH_SITUATION trimmed), `vehicle_category` (CATV_VEH).
 *
 * « Sortir » / « Déclarer un sinistre » / document download are out of scope
 * (no spec) — nothing rendered for them.
 */

import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Badge } from '@components/base/display';
import { Tabs } from '@components/base/navigation';
import { RuleContextProvider } from '@components/shared/configurable';

import { PageHeader } from '../components/PageHeader';
import { VEHICLE_PAGE_TEXT, VEHICLE_TABS, type VehicleTabKey } from '../components/fleet/fleetText';
import {
  VehicleClaimsCardConfigurable as VehicleClaimsCard,
  VehicleContractCardConfigurable as VehicleContractCard,
  VehicleDetailsCardConfigurable as VehicleDetailsCard,
  VehicleDocumentsCardConfigurable as VehicleDocumentsCard,
  VehicleGuaranteesCardConfigurable as VehicleGuaranteesCard,
} from '../components/fleet/register';
import { useVehicleDetailData } from '../components/fleet/useFleetQueries';
import { buildVehicleRuleContext } from '../components/fleet/vehicleContext';
import { vehicleContractValues, vehicleDetailsValues } from '../components/fleet/vehicleInfoConfig';
import { readVehicleRouteState } from '../components/fleet/vehicleNavigation';
import { useExtranetBaseRuleContext } from '../contextVariables';
import { EXTRANET_PAGE_TEXT, extranetPath } from '../navigation';

const DEFAULT_TAB: VehicleTabKey = 'details';

export function VehicleDetailPage() {
  const {
    tenantSlug,
    idNpol = '',
    idVeh = '',
  } = useParams<{ tenantSlug: string; idNpol: string; idVeh: string }>();
  const location = useLocation();
  const routeState = readVehicleRouteState(location.state);
  const data = useVehicleDetailData(idNpol, idVeh);
  const details = data.details.data;
  const text = EXTRANET_PAGE_TEXT.vehicle;

  // --- Header -------------------------------------------------------------
  const listPath = extranetPath(tenantSlug, 'flotte');
  const marqueModele = [details?.marque, details?.modele]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .join(' ');
  const title = marqueModele || routeState.marqueModele || text.title;
  const immat = details?.immatriculation ?? routeState.immat;

  // --- Rule context ---------------------------------------------------------
  const baseContext = useExtranetBaseRuleContext();
  const vehicleStatus = details?.statut ?? null;
  const vehicleCategory = details?.categorie ?? null;
  const ruleContext = useMemo(
    () =>
      buildVehicleRuleContext(
        baseContext,
        { idNpol, idVeh },
        { statut: vehicleStatus, categorie: vehicleCategory },
      ),
    [baseContext, idNpol, idVeh, vehicleStatus, vehicleCategory],
  );

  // --- Card data ------------------------------------------------------------
  const detailsValues = useMemo(() => vehicleDetailsValues(details), [details]);
  const contractValues = useMemo(
    () => vehicleContractValues(data.contract.data),
    [data.contract.data],
  );
  const contractPath = idNpol === '' ? null : extranetPath(tenantSlug, 'contrats', idNpol);
  const claimRows = useMemo(
    () =>
      data.claims.data.map((claim) => ({
        ...claim,
        to: claim.idSin === null ? null : extranetPath(tenantSlug, 'sinistres', claim.idSin),
      })),
    [data.claims.data, tenantSlug],
  );
  const counts: Partial<Record<VehicleTabKey, number>> = {
    sinistres: data.claims.loading || data.claims.error ? 0 : data.claims.data.length,
  };

  return (
    <RuleContextProvider value={ruleContext}>
      <div data-testid="extranet-page-vehicle">
        <PageHeader
          title={title}
          subtitle={immat ?? undefined}
          backTo={listPath}
          breadcrumbs={[
            { key: 'flotte', label: text.breadcrumb, to: listPath },
            { key: 'vehicule', label: immat ?? idVeh },
          ]}
        />
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <Tabs defaultValue={DEFAULT_TAB}>
            <Tabs.List aria-label={VEHICLE_PAGE_TEXT.tabsLabel}>
              {VEHICLE_TABS.map((tab) => {
                const count = tab.showCount ? (counts[tab.key] ?? 0) : 0;
                return (
                  <Tabs.Tab key={tab.key} value={tab.key}>
                    {tab.label}
                    {count > 0 && (
                      <Badge
                        size="sm"
                        pill
                        className="ml-1.5"
                        data-testid={`vehicle-tab-count-${tab.key}`}
                      >
                        {count}
                      </Badge>
                    )}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>

            <Tabs.Panel value="details">
              <VehicleDetailsCard
                values={detailsValues}
                loading={data.details.loading}
                error={data.details.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="contrat">
              <VehicleContractCard
                values={contractValues}
                loading={data.contract.loading}
                error={data.contract.error}
                contractPath={contractPath}
              />
            </Tabs.Panel>
            <Tabs.Panel value="garanties">
              <VehicleGuaranteesCard
                rows={data.guarantees.data}
                loading={data.guarantees.loading}
                error={data.guarantees.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="sinistres">
              <VehicleClaimsCard
                rows={claimRows}
                loading={data.claims.loading}
                error={data.claims.error}
              />
            </Tabs.Panel>
            <Tabs.Panel value="documents">
              <VehicleDocumentsCard
                documents={data.documents.data}
                loading={data.documents.loading}
                error={data.documents.error}
              />
            </Tabs.Panel>
          </Tabs>
        </div>
      </div>
    </RuleContextProvider>
  );
}
